import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import {Acuity, ArrivalMode, AttendanceSource, ChiefOfComplaintECDS} from '../../../../controls/agyle/ecds';
import moment from 'moment';
import config from '../../../../app/config';
import _ from 'lodash';
import {AttendanceCategory, ConsultationMechanism} from '../../../../controls/agyle/dictionaries';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {
    acuity,
    ambulanceCodes,
    arrivalModes,
    attendanceCategory,
    attendanceSource,
    chiefComplaint,
    consultationMechanism,
} from '../../../../store/reducers/dicts';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {cleanObject} from '../../../../util/patientContextUtil';

const AttendanceActivityV4 = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version, isSmall} = props;

    //Attendance Activity Characteristics Dataset
    const [attendanceActivity, setAttendanceActivity] = useState({
        UrgentAndEmergencyCareActivityIdentifier: '',
        ConsultationMechanism_UrgentAndEmergencyCare: null,
        UrgentAndEmergencyCareArrivalMode_SnomedCt: null,
        UrgentAndEmergencyCareAttendanceCategory: null,
        UrgentAndEmergencyCareAttendanceSource_SnomedCt: null,
        OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource: null,
        UrgentAndEmergencyCareActivityStartDateAndTime: null,
        AgeAtCDSActivityDate: '',
        UrgentAndEmergencyCareInitialAssessmentTimestamp: null,
        UrgentAndEmergencyCareAcuity_SnomedCt: null,
        UrgentAndEmergencyCareChiefComplaint_SnomedCt: null,
        UrgentAndEmergencyCareTimestampSeenForTreatment: null,
        UrgentAndEmergencyCareExtendedCareEpisodeIdentifier: null,
    });

    const [pickers, setPickers] = useState({
        UrgentAndEmergencyCareActivityStartDateAndTime: null,
        UrgentAndEmergencyCareInitialAssessmentTimestamp: null,
        UrgentAndEmergencyCareTimestampSeenForTreatment: null,
    });

    // Dictionaries
    const acuityDict = useSelector(acuity);
    const arrivalModesDict = useSelector(arrivalModes);
    const attendanceSourceDict = useSelector(attendanceSource);
    const attendanceCategoryDict = useSelector(attendanceCategory);
    const chiefComplaintDict = useSelector(chiefComplaint);
    const consultationMechanismDict = useSelector(consultationMechanism);
    const ambulance = useSelector(ambulanceCodes);

    // For ReadOnly Data
    const disableData = useRef({});

    // Validation
    const validationEnabled = config?.modules?.forms?.ecds?.validation || false;
    const [error, setError] = useState({
        attendanceId: validationEnabled,
        episodeId: false,
        arrival: false,
        mechanism: false,
        activityDateTime: validationEnabled,
        attendanceCat: false,
        attendanceSource: false,
        orgId: false,
        age: validationEnabled,
        acuity: false,
        chiefOfComplaint: false,
        assessmentDateTime: false,
        seenDateTime: false,
    });

    // invalid pickers
    const errorPicker = useRef({
        activityDateTime: null,
        assessmentDateTime: null,
        seenDateTime: null,
    });

    // Initial Data
    useEffect(() => {
        let pickers = {};
        let data = {};
        let dataToBlock;

        if (props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.UrgentAndEmergencyCareActivityCharacteristics) {
            const clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.AttendanceOccurrence?.UrgentAndEmergencyCareActivityCharacteristics);

            data = {
                UrgentAndEmergencyCareActivityIdentifier: clone.UrgentAndEmergencyCareActivityIdentifier || '',
                ConsultationMechanism_UrgentAndEmergencyCare: !_.isEmpty(clone.ConsultationMechanism_UrgentAndEmergencyCare) ? _.find(consultationMechanismDict, {__code: clone.ConsultationMechanism_UrgentAndEmergencyCare}) : null,
                UrgentAndEmergencyCareArrivalMode_SnomedCt: !_.isEmpty(clone.UrgentAndEmergencyCareArrivalMode_SnomedCt) ? _.find(arrivalModesDict, {
                    ecdsVersion: version,
                    snomedCodes: clone.UrgentAndEmergencyCareArrivalMode_SnomedCt,
                }) : null,
                UrgentAndEmergencyCareAttendanceCategory: !_.isEmpty(clone.UrgentAndEmergencyCareAttendanceCategory) ? _.find(attendanceCategoryDict, {__code: clone.UrgentAndEmergencyCareAttendanceCategory}) : null,
                UrgentAndEmergencyCareAttendanceSource_SnomedCt: !_.isEmpty(clone.UrgentAndEmergencyCareAttendanceSource_SnomedCt) ? _.find(attendanceSourceDict, {
                    ecdsVersion: version,
                    snomedCodes: clone.UrgentAndEmergencyCareAttendanceSource_SnomedCt,
                }) : null,
                OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource: clone.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource || '',
                UrgentAndEmergencyCareActivityStartDateAndTime: clone.UrgentAndEmergencyCareActivityStartDateAndTime ? moment(clone.UrgentAndEmergencyCareActivityStartDateAndTime) : null,
                AgeAtCDSActivityDate: clone.AgeAtCDSActivityDate,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: clone.UrgentAndEmergencyCareInitialAssessmentTimestamp ? moment(clone.UrgentAndEmergencyCareInitialAssessmentTimestamp) : null,
                UrgentAndEmergencyCareAcuity_SnomedCt: !_.isEmpty(clone.UrgentAndEmergencyCareAcuity_SnomedCt) ? _.find(acuityDict, {
                    ecdsVersion: version,
                    snomedCodes: clone.UrgentAndEmergencyCareAcuity_SnomedCt,
                }) : null,
                UrgentAndEmergencyCareChiefComplaint_SnomedCt: !_.isEmpty(clone.UrgentAndEmergencyCareChiefComplaint_SnomedCt) ? _.find(chiefComplaintDict, {
                    ecdsVersion: version,
                    snomedCodes: clone.UrgentAndEmergencyCareChiefComplaint_SnomedCt,
                }) : null,
                UrgentAndEmergencyCareTimestampSeenForTreatment: clone.UrgentAndEmergencyCareTimestampSeenForTreatment ? moment(clone.UrgentAndEmergencyCareTimestampSeenForTreatment) : null,
                UrgentAndEmergencyCareExtendedCareEpisodeIdentifier: clone.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier || '',
            };

            pickers = {
                UrgentAndEmergencyCareActivityStartDateAndTime: clone.UrgentAndEmergencyCareActivityStartDateAndTime ? moment(clone.UrgentAndEmergencyCareActivityStartDateAndTime) : null,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: clone.UrgentAndEmergencyCareInitialAssessmentTimestamp ? moment(clone.UrgentAndEmergencyCareInitialAssessmentTimestamp) : null,
                UrgentAndEmergencyCareTimestampSeenForTreatment: clone.UrgentAndEmergencyCareTimestampSeenForTreatment ? moment(clone.UrgentAndEmergencyCareTimestampSeenForTreatment) : null,
            };
        }

        const arrival = props.patient.admission?.arrivalModeCode;
        const isAmb = !_.isEmpty(ambulance) && ambulance.includes(arrival);
        const arrivalDate = isAmb ? moment(calculateArrivalDate(props.patient), config.defaultDateTimeFormat) : moment(props.patient?.admission?.startDate);

        dataToBlock = {
            UrgentAndEmergencyCareActivityStartDateAndTime: arrivalDate,
            ConsultationMechanism_UrgentAndEmergencyCare: _.find(consultationMechanismDict, {__code: '01'}) || null, // TODO For now hardcode
        };

        if (acuityDict && props.patient?.triage?.firstDiscriminator) {
            const priority = props.patient.triage.firstDiscriminator?.snomedCode;
            let acuity = _.find(acuityDict, a => _.startsWith(a.ecdsVersion === version && a.snomedCode, priority));
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareAcuity_SnomedCt: acuity,
            };
        }

        if (arrivalModesDict && props.patient.admission?.arrivalModeAltCode) {
            const arrival = props.patient.admission?.arrivalModeAltCode;
            let result = _.find(arrivalModesDict, am => am.ecdsVersion === version && _.startsWith(am.snomedCode, arrival));
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareArrivalMode_SnomedCt: result,
            };
        }

        if (attendanceSourceDict && props.patient.admission?.admitSourceCode) {
            let result = _.find(attendanceSourceDict, {
                ecdsVersion: version,
                snomedCode: props.patient.admission?.admitSourceCode,
            });
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareAttendanceSource_SnomedCt: result || null,
            };
        }

        if (chiefComplaintDict && props.patient.admission?.initialAEComplaintSnomedCode && !attendanceActivity.EmergencyCareChiefComplaint_SnomedCt) {
            const chief = props.patient.admission?.initialAEComplaintSnomedCode;
            let result = null;
            if (chief) {
                chiefComplaintDict.forEach((el) => {
                    if (el.ecdsVersion === version && _.startsWith(el.snomedCode, chief)) {
                        result = el;
                    }
                });
            }
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareChiefComplaint_SnomedCt: result,
            };
        }

        if (props.patient.patient?.dob) {
            dataToBlock = {
                ...dataToBlock,
                AgeAtCDSActivityDate: arrivalDate.diff(moment(props.patient.patient.dob), 'years') + '',
            };
        }

        if (props.patient.admission) {
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareActivityIdentifier: props.patient.admission.attendanceId || '',
                UrgentAndEmergencyCareExtendedCareEpisodeIdentifier: '',
            };
        }

        if (props.patient.triage) {
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareChiefComplaint_SnomedCt: props.patient.triage.chiefComplaint || null,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
            };

            pickers = {
                ...pickers,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: props.patient.triage.firstWhen ? moment(props.patient.triage.firstWhen) : null,
            };
        }

        // From our registration page
        if (props.patient?.metadata?.attendanceCategory) {
            const find = _.find(attendanceCategoryDict, {__code: props.patient?.metadata?.attendanceCategory?.code});
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareAttendanceCategory: find || null,
            };
        }

        // KPI Time to Initial Assessment
        // Triage Start Time
        // Ambulance Handover Time
        // See and Treat Time
        // Rapid Assessment Time
        if (props.patient?.metadata?.firstInitialAssessment?.when) {
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
            };

            pickers = {
                ...pickers,
                UrgentAndEmergencyCareInitialAssessmentTimestamp: moment(props.patient?.metadata?.firstInitialAssessment?.when) || null,
            };
        }

        // KPI Time to Treatment
        // See and Treat Time
        // Rapid Assessment Time
        // Clerking Clinician Set Time
        // Clerking Misinform started time
        if (props.patient?.metadata?.firstClinicianSaved?.when) {
            dataToBlock = {
                ...dataToBlock,
                UrgentAndEmergencyCareTimestampSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
            };

            pickers = {
                ...pickers,
                UrgentAndEmergencyCareTimestampSeenForTreatment: moment(props.patient?.metadata?.firstClinicianSaved?.when) || null,
            };
        }

        data = {
            ...data,
            ...dataToBlock,
        };

        setAttendanceActivity(prevState => ({...prevState, ...data}));
        disableData.current = {...cleanObject(dataToBlock)};
        setPickers({...pickers});
    }, []);

    const setDataDictionary = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const updateData = () => {
        const activity = attendanceActivity?.UrgentAndEmergencyCareActivityStartDateAndTime ? moment(attendanceActivity.UrgentAndEmergencyCareActivityStartDateAndTime).toISOString() : '';
        const initial = attendanceActivity?.UrgentAndEmergencyCareInitialAssessmentTimestamp ? moment(attendanceActivity.UrgentAndEmergencyCareInitialAssessmentTimestamp).toISOString() : '';
        const seen = attendanceActivity?.UrgentAndEmergencyCareTimestampSeenForTreatment ? moment(attendanceActivity.UrgentAndEmergencyCareTimestampSeenForTreatment).toISOString() : '';

        const data = {
            UrgentAndEmergencyCareActivityIdentifier: attendanceActivity?.UrgentAndEmergencyCareActivityIdentifier || '',
            ConsultationMechanism_UrgentAndEmergencyCare: attendanceActivity?.ConsultationMechanism_UrgentAndEmergencyCare?.__code || '',
            UrgentAndEmergencyCareArrivalMode_SnomedCt: attendanceActivity?.UrgentAndEmergencyCareArrivalMode_SnomedCt?.snomedCode || '',
            UrgentAndEmergencyCareAttendanceCategory: attendanceActivity?.UrgentAndEmergencyCareAttendanceCategory?.__code || '',
            UrgentAndEmergencyCareAttendanceSource_SnomedCt: attendanceActivity?.UrgentAndEmergencyCareAttendanceSource_SnomedCt?.snomedCode || '',
            OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource: attendanceActivity?.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource || '',
            UrgentAndEmergencyCareActivityStartDateAndTime: activity,
            AgeAtCDSActivityDate: attendanceActivity?.AgeAtCDSActivityDate || '',
            UrgentAndEmergencyCareInitialAssessmentTimestamp: initial,
            UrgentAndEmergencyCareAcuity_SnomedCt: attendanceActivity?.UrgentAndEmergencyCareAcuity_SnomedCt?.snomedCode || '',
            UrgentAndEmergencyCareChiefComplaint_SnomedCt: attendanceActivity?.UrgentAndEmergencyCareChiefComplaint_SnomedCt?.snomedCode || '',
            UrgentAndEmergencyCareTimestampSeenForTreatment: seen,
            UrgentAndEmergencyCareExtendedCareEpisodeIdentifier: attendanceActivity?.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier || '',
        };
        props.setData(data);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (validate()) {
                updateData();
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [attendanceActivity, pickers]);

    const changeHandler = (event) => {
        setAttendanceActivity((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const validate = () => {
        let check = true;

        // TODO Validacja
        if (validationEnabled) {
            const errorData = {
                attendanceId: !props.checkLenght(1, 12, attendanceActivity.UrgentAndEmergencyCareActivityIdentifier),
                episodeId: attendanceActivity.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier ? !props.checkLenght(1, 20, attendanceActivity.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier) : false,
                arrival: attendanceActivity.UrgentAndEmergencyCareArrivalMode_SnomedCt ? !props.checkLenght(6, 18, attendanceActivity.UrgentAndEmergencyCareArrivalMode_SnomedCt) : false,
                mechanism: attendanceActivity.ConsultationMechanism_UrgentAndEmergencyCare ? !props.checkLenght(1, 2, attendanceActivity.ConsultationMechanism_UrgentAndEmergencyCare.__code) : false,
                activityDateTime: !attendanceActivity.UrgentAndEmergencyCareActivityStartDateAndTime,
                attendanceCat: attendanceActivity.UrgentAndEmergencyCareAttendanceCategory ? !props.checkLenght(1, 1, attendanceActivity.UrgentAndEmergencyCareAttendanceCategory.__code) : false,
                attendanceSource: attendanceActivity.UrgentAndEmergencyCareAttendanceSource_SnomedCt ? !props.checkLenght(6, 18, attendanceActivity.UrgentAndEmergencyCareAttendanceSource_SnomedCt) : false,
                orgId: attendanceActivity.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource !== '' ? !props.checkLenght(5, 9, attendanceActivity.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource) : false,
                age: !props.checkLenght(1, 3, attendanceActivity.AgeAtCDSActivityDate),
                acuity: attendanceActivity.UrgentAndEmergencyCareAcuity_SnomedCt ? !props.checkLenght(6, 18, attendanceActivity.UrgentAndEmergencyCareAcuity_SnomedCt) : false,
                chiefOfComplaint: attendanceActivity.UrgentAndEmergencyCareChiefComplaint_SnomedCt ? !props.checkLenght(6, 18, attendanceActivity.UrgentAndEmergencyCareChiefComplaint_SnomedCt) : false,
            };
            setError(prevState => ({...prevState, ...errorData}));

            for (let prop in errorData) {
                if (errorData[prop]) {
                    check = false;
                    break;
                }
            }

            if (props.valid) {
                const validObj = {
                    name: 'Urgent And Emergency Care Attendance Activity Characteristics',
                    groupStatus: 'M',
                    mandatory: !attendanceActivity.UrgentAndEmergencyCareActivityIdentifier || !attendanceActivity.AgeAtCDSActivityDate
                        || !attendanceActivity.UrgentAndEmergencyCareActivityStartDateAndTime,
                    errors: !check,
                    pickers: !!errorPicker.current.seenDateTime || !!errorPicker.current.assessmentDateTime
                        || !!errorPicker.current.activityDateTime,
                };
                props.valid(prevState => ({...prevState, 'activityChar': validObj}));
            }
        }
        return check;
    };

    const dateHandler = (name, event) => {
        const date = moment(event).set('second', 0);
        setPickers((prevState) => ({...prevState, [name]: date}));
    };

    if (props.accordion !== 'Urgent And Emergency Care Attendance Activity Characteristics') return null;

    return (
        <div className={classes.root}>
            <Grid container spacing={2} style={{marginTop: 1}}>
                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="UrgentAndEmergencyCareActivityIdentifier"
                        label={t('Attendance Identifier')}
                        onChange={changeHandler}
                        value={attendanceActivity.UrgentAndEmergencyCareActivityIdentifier || ''}
                        error={error.attendanceId}
                        helperText={error.attendanceId ? 'Input must be between 1 and 12 characters long.' : ''}
                        maxLength={12}
                        minLength={1}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareActivityIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="UrgentAndEmergencyCareExtendedCareEpisodeIdentifier"
                        label={t('Episode Identifier')}
                        onChange={changeHandler}
                        value={attendanceActivity.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier || ''}
                        error={error.attendanceId}
                        helperText={error.attendanceId ? 'Input must be between 1 and 20 characters long.' : ''}
                        maxLength={20}
                        minLength={1}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareExtendedCareEpisodeIdentifier}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <ArrivalMode
                        name="UrgentAndEmergencyCareArrivalMode_SnomedCt"
                        label={t('Arrival Mode')}
                        color="primary"
                        value={attendanceActivity.UrgentAndEmergencyCareArrivalMode_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['UrgentAndEmergencyCareArrivalMode_SnomedCt', val])}
                        error={error.arrival}
                        disabled={!!disableData?.current.UrgentAndEmergencyCareArrivalMode_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <ConsultationMechanism
                        name="ConsultationMechanism_UrgentAndEmergencyCare"
                        label={t('Consultation Mechanism')}
                        color="primary"
                        value={attendanceActivity.ConsultationMechanism_UrgentAndEmergencyCare || null}
                        onChange={(_e, val) => setDataDictionary(['ConsultationMechanism_UrgentAndEmergencyCare', val])}
                        error={error.arrival}
                        disabled={!!disableData?.current.ConsultationMechanism_UrgentAndEmergencyCare}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="UrgentAndEmergencyCareActivityStartDateAndTime"
                        label={t('Activity Start Date And Time')}
                        type="dateTime"
                        value={attendanceActivity.UrgentAndEmergencyCareActivityStartDateAndTime || null}
                        onChange={() => null}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            activityDateTime: event,
                        }}
                        error={error.activityDateTime}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareActivityStartDateAndTime}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AttendanceCategory
                        name="UrgentAndEmergencyCareAttendanceCategory"
                        label={t('Attendance Category')}
                        color="primary"
                        value={attendanceActivity.UrgentAndEmergencyCareAttendanceCategory}
                        disabled={!!disableData?.current.UrgentAndEmergencyCareAttendanceCategory}
                        onChange={(_e, val) => setDataDictionary(['UrgentAndEmergencyCareAttendanceCategory', val])}
                        error={error.attendanceCat}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AttendanceSource
                        name="UrgentAndEmergencyCareAttendanceSource_SnomedCt"
                        label={t('Attendance Source')}
                        color="primary"
                        value={attendanceActivity.UrgentAndEmergencyCareAttendanceSource_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['UrgentAndEmergencyCareAttendanceSource_SnomedCt', val])}
                        error={error.attendanceSource}
                        disabled={!!disableData?.current.UrgentAndEmergencyCareAttendanceSource_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource"
                        label={t('Organisation Site Identifier')}
                        onChange={changeHandler}
                        value={attendanceActivity.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource || ''}
                        error={error.orgId}
                        helperText={error.orgId ? 'Input must be between 5 and 9 characters long.' : ''}
                        maxLength={9}
                        minLength={5}
                        readOnly={!!disableData?.current.OrganisationSiteIdentifier_UrgentAndEmergencyCareAttendanceSource}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="AgeAtCDSActivityDate"
                        label={t('Age at CDS Activity Date')}
                        type="number"
                        onChange={changeHandler}
                        value={attendanceActivity.AgeAtCDSActivityDate || ''}
                        error={error.age}
                        helperText={error.age ? 'Input must be max 3 characters long.' : ''}
                        readOnly={!!disableData?.current.AgeAtCDSActivityDate}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <ChiefOfComplaintECDS
                        name="UrgentAndEmergencyCareChiefComplaint_SnomedCt"
                        label={t('Chief Complaint')}
                        color="primary"
                        value={attendanceActivity.UrgentAndEmergencyCareChiefComplaint_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['UrgentAndEmergencyCareChiefComplaint_SnomedCt', val])}
                        error={error.chiefOfComplaint}
                        disabled={!!disableData?.current.UrgentAndEmergencyCareChiefComplaint_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Acuity
                        name="UrgentAndEmergencyCareAcuity_SnomedCt"
                        label={t('Acuity')}
                        color="primary"
                        value={attendanceActivity.UrgentAndEmergencyCareAcuity_SnomedCt || null}
                        onChange={(_e, val) => setDataDictionary(['UrgentAndEmergencyCareAcuity_SnomedCt', val])}
                        error={error.acuity}
                        disabled={!!disableData?.current.UrgentAndEmergencyCareAcuity_SnomedCt}
                        admitDate={props.patient?.admission?.startDate}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="UrgentAndEmergencyCareInitialAssessmentTimestamp"
                        label={t('Initial Assessment Timestamp')}
                        type="dateTime"
                        value={pickers.UrgentAndEmergencyCareInitialAssessmentTimestamp || null}
                        onChange={(e) => dateHandler('UrgentAndEmergencyCareInitialAssessmentTimestamp', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            assessmentDateTime: event,
                        }}
                        error={error.assessmentDateTime}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareInitialAssessmentTimestamp}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker
                        name="UrgentAndEmergencyCareTimestampSeenForTreatment"
                        label={t('Seen for Treatment Timestamp')}
                        type="dateTime"
                        value={pickers.UrgentAndEmergencyCareTimestampSeenForTreatment || null}
                        onChange={(e) => dateHandler('UrgentAndEmergencyCareTimestampSeenForTreatment', e)}
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        onError={event => errorPicker.current = {
                            ...errorPicker.current,
                            seenDateTime: event,
                        }}
                        error={error.seenDateTime}
                        readOnly={!!disableData?.current.UrgentAndEmergencyCareTimestampSeenForTreatment}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default AttendanceActivityV4;